/**
 * Wer benutzt die App gerade – und was darf er?
 *
 * WAS HIER EHRLICH GESAGT WERDEN MUSS
 * -----------------------------------
 * Diese App hat kein Backend und keine Anmeldung. Alle Daten liegen in der
 * IndexedDB des Geraets. Eine Sperre, die nur im Browser laeuft, ist deshalb
 * KEINE Sicherheitsgrenze: wer das iPad in der Hand haelt, kommt ohnehin an
 * alles heran. Der Schalter unten ist also ein SICHTBARKEITS-Schalter, der
 * den Wartungsbereich aus dem Weg raeumt, und wird auch genau so benannt.
 * Er soll nichts vortaeuschen, was er nicht leisten kann.
 *
 * WIE DIE ANMELDUNG SPAETER HINEINKOMMT
 * -------------------------------------
 * Die Oberflaeche fragt nie nach einer Rolle, sondern immer nach einer
 * Faehigkeit ("darf ich den Wartungsbereich sehen?"). Beantwortet wird das
 * von einem austauschbaren Provider. Kommt spaeter eine echte Anmeldung,
 * ruft man einmal setAuthProvider(...) auf – an den Ansichten aendert sich
 * nichts, und erst dann wird aus der Sichtbarkeit eine echte Pruefung.
 */
import type { Capability, Role, User } from './roles.ts';
import { levelOf, roleCan } from './roles.ts';

/** Woher die App erfaehrt, wer gerade da ist. */
export interface AuthProvider {
  /** Der angemeldete Benutzer, oder null wenn niemand angemeldet ist. */
  currentUser(): Promise<User | null>;
  /** Alle bekannten Benutzer – fuer die Verwaltung im Wartungsbereich. */
  listUsers(): Promise<User[]>;
  /** true, wenn dieser Provider echte Anmeldung leistet. */
  readonly authenticated: boolean;
  /** Kurze Beschreibung fuer den Wartungsbereich. */
  readonly name: string;
  /** E-Mail des angemeldeten Kontos, falls es eine gibt. */
  email?(): string | null;
  /** Abmelden. Fehlt bei Providern ohne Anmeldung. */
  signOut?(): Promise<void>;
  /** Rolle, Freischaltung oder Sperre eines Benutzers aendern (nur Administratoren). */
  updateUser?(id: string, patch: Partial<Pick<User, 'role' | 'active' | 'blocked' | 'level' | 'areas'>>): Promise<void>;
}

const UNLOCK_KEY = 'leadsheet:maintenance';

/**
 * Der Provider fuer den Betrieb ohne Anmeldung.
 *
 * Es gibt genau einen Benutzer: den Menschen, der das Geraet in der Hand
 * haelt. Seine Rolle haengt am Schalter unten – mehr kann eine App ohne
 * Server ehrlicherweise nicht wissen.
 */
const localProvider: AuthProvider = {
  name: 'Gerät (ohne Anmeldung)',
  authenticated: false,

  async currentUser(): Promise<User | null> {
    return {
      id: 'local',
      name: 'Dieses Gerät',
      role: isMaintenanceUnlocked() ? 'admin' : 'member',
      active: true,
    };
  },

  async listUsers(): Promise<User[]> {
    const user = await localProvider.currentUser();
    return user ? [user] : [];
  },
};

let provider: AuthProvider = localProvider;

/**
 * Haengt eine echte Anmeldung ein.
 * Genau ein Aufruf beim Start der App genuegt; alles andere bleibt, wie es ist.
 */
export function setAuthProvider(next: AuthProvider): void {
  provider = next;
}

export function authProvider(): AuthProvider {
  return provider;
}

export function currentUser(): Promise<User | null> {
  return provider.currentUser();
}

export function listUsers(): Promise<User[]> {
  return provider.listUsers();
}

/**
 * Die einzige Frage, die die Oberflaeche stellen soll.
 * Ein deaktivierter Benutzer darf gar nichts.
 */
export async function can(capability: Capability): Promise<boolean> {
  const user = await provider.currentUser();
  if (!user || !user.active) return false;
  // Stufen (roles.ts): anlegen/aendern ab 2, loeschen ab 3.
  if (capability === 'content.edit' && levelOf(user) < 2) return false;
  if (capability === 'content.delete' && levelOf(user) < 3) return false;
  return roleCan(user.role, capability);
}

export async function currentRole(): Promise<Role | null> {
  const user = await provider.currentUser();
  return user?.role ?? null;
}

/* ------------------------------------------------- Der Sichtbarkeitsschalter */

/**
 * Bewusst im localStorage und nicht in den App-Einstellungen: Er gehoert zum
 * Geraet, nicht zu den Daten, und soll deshalb weder im Backup landen noch
 * beim Einspielen eines fremden Backups mitkommen.
 */
export function isMaintenanceUnlocked(): boolean {
  try {
    return localStorage.getItem(UNLOCK_KEY) === 'on';
  } catch {
    // Gesperrter Speicher ist kein Fehlerfall – dann eben zu.
    return false;
  }
}

export function setMaintenanceUnlocked(on: boolean): void {
  try {
    if (on) localStorage.setItem(UNLOCK_KEY, 'on');
    else localStorage.removeItem(UNLOCK_KEY);
  } catch {
    /* egal */
  }
  for (const listener of listeners) listener();
}

type Listener = () => void;
const listeners = new Set<Listener>();

/** Fuer Provider: die Rechte haben sich geaendert, die Oberflaeche soll neu fragen. */
export function notifyAccessChange(): void {
  for (const listener of listeners) listener();
}

/** Meldet sich, wenn sich an den Rechten etwas aendert (fuer die Navigation). */
export function onAccessChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
